/**
 * NOTE: This file is meant to be removed over time.  This was a quick copy/paste/edit to
 * handle all the needs of the race listing
 // TODO new note about this component
 */
import axios, { Canceler } from 'axios';
import { groupBy, keyBy, orderBy } from 'lodash';
import React, { useState } from 'react';
import RaceFilterListingItemContent from '~/tools/js/CharacterBuilder/components/RaceFilterListingItemContent';

import { DisabledChevronDownSvg, DisabledChevronUpSvg, LoadingPlaceholder } from '@dndbeyond/character-components/es';
import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiAdapterUtils,
    ApiResponse,
    Race,
    RaceDefinitionContract,
    RaceGroupContract,
    RaceUtils,
    RuleData,
    RuleDataUtils,
} from '@dndbeyond/character-rules-engine/es';

import { AppLoggerUtils, FilterUtils } from '../../utils';

const FILTER_LIST_ITEM_TYPE = {
    GROUP: 1,
    ITEM: 2,
};

interface RaceListingItemProps {
    race: RaceDefinitionContract;
    onRaceSelected: (race: RaceDefinitionContract) => void;
    disabled: boolean;
    ruleData: RuleData;
    defaultImageUrl: string;
}
const FilterListingItem: React.FC<RaceListingItemProps> = ({
    ruleData,
    race,
    onRaceSelected,
    disabled,
    defaultImageUrl,
}) => {
    let clsNames: Array<string> = ['filter-listing-item', 'filter-listing-entry'];
    if (disabled) {
        clsNames.push('filter-listing-item-disabled');
    }

    return (
        <div className={clsNames.join(' ')} onClick={disabled ? undefined : () => onRaceSelected(race)}>
            <RaceFilterListingItemContent ruleData={ruleData} race={race} defaultImageUrl={defaultImageUrl} />
        </div>
    );
};

interface RaceListingGroupProps {
    onRaceSelected: (race: RaceDefinitionContract) => void;
    ruleData: RuleData;
    items: Array<RaceItemsLazyOrFilteredState>;
    defaultImageUrl: string;
    name: string | null;
    avatarUrl: string | null;
}
const FilterListingGroup: React.FC<RaceListingGroupProps> = ({
    ruleData,
    items,
    defaultImageUrl,
    onRaceSelected,
    name,
    avatarUrl,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    let image = avatarUrl;
    if (!image) {
        image = defaultImageUrl;
    }

    let classNames: Array<string> = ['filter-listing-group', 'filter-listing-entry'];
    if (!isCollapsed) {
        classNames.push('filter-listing-group-opened');
    }

    return (
        <div className={classNames.join(' ')}>
            <div className="filter-listing-item" onClick={() => setIsCollapsed(!isCollapsed)}>
                <div className="filter-listing-item-content">
                    <div className="filter-listing-item-heading">
                        <div className="filter-listing-group-header">
                            <div className="filter-listing-group-preview">
                                <img className="filter-listing-group-preview-img" src={image} alt="" />
                            </div>
                            <div className="filter-listing-group-content">
                                <div className="filter-listing-group-parent">{name}</div>
                                <div className="filter-listing-group-info">({items.length})</div>
                            </div>
                            <div className="filter-listing-group-action">
                                {isCollapsed ? <DisabledChevronDownSvg /> : <DisabledChevronUpSvg />}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {!isCollapsed && (
                <div className="filter-listing-group-items">
                    {items.map((listItem) => (
                        <FilterListingItem
                            race={listItem.entity}
                            disabled={listItem.disabled}
                            key={listItem.id}
                            onRaceSelected={onRaceSelected}
                            ruleData={ruleData}
                            defaultImageUrl={defaultImageUrl}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const HOMEBREW_ID = 'HOMEBREW';

interface RaceItemsLazyOrFilteredState {
    type: string;
    id: string;
    entityTypeId: number;
    name: string | null;
    disabled: boolean;
    entity: RaceDefinitionContract;
    groupId?: number;
}
interface FilteredRaceItem {
    item: RaceItemsLazyOrFilteredState;
    key: string;
    sortString: string;
    type: number;
}
interface FilteredRaceGroup extends RaceGroupContract {
    items: Array<RaceItemsLazyOrFilteredState>;
    key: string;
    sortString: string;
    type: number;
}
interface Props {
    loadRaces: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<RaceDefinitionContract>>>;
    onLoadRaces: (data: Array<RaceDefinitionContract>) => void;
    onRaceSelected: (race: RaceDefinitionContract) => void;
    ruleData: RuleData;
    currentRace?: Race | null;
    currentSourceFilter: string | null;
}
interface State {
    query: string;
    lazyItems: Array<RaceItemsLazyOrFilteredState>;
    filteredGroups: Array<FilteredRaceGroup | FilteredRaceItem>;
    itemsLoading: boolean;
    itemsLoaded: boolean;
}
export default class RaceFilterListing extends React.PureComponent<Props, State> {
    loadItemsCanceler: null | Canceler = null;
    listingContent = React.createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);

        this.state = {
            query: '',
            lazyItems: [],
            filteredGroups: [],
            itemsLoading: false,
            itemsLoaded: false,
        };
    }

    componentDidMount() {
        const { loadRaces, onLoadRaces } = this.props;

        this.setState({
            itemsLoading: true,
        });

        loadRaces({
            cancelToken: new axios.CancelToken((c) => {
                this.loadItemsCanceler = c;
            }),
        })
            .then((response) => {
                let data = ApiAdapterUtils.getResponseData(response);
                let transformedItems = this.disableItems(this.transformLoadedRaces(data));
                let transformedGroups = this.groupItems(transformedItems);
                onLoadRaces(data ?? []);
                this.setState({
                    lazyItems: transformedItems,
                    filteredGroups: transformedGroups,
                    itemsLoaded: true,
                    itemsLoading: false,
                });
                this.loadItemsCanceler = null;
            })
            .catch(AppLoggerUtils.handleAdhocApiError);
    }

    componentWillUnmount(): void {
        if (this.loadItemsCanceler !== null) {
            this.loadItemsCanceler();
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        const { loadRaces, currentSourceFilter } = this.props;

        if (loadRaces !== prevProps.loadRaces || currentSourceFilter !== prevProps.currentSourceFilter) {
            this.setState((prevState) => ({
                lazyItems: this.disableItems(prevState.lazyItems),
                filteredGroups: this.groupItems(this.disableItems(this.getFilteredItems(''))),
            }));
        }
    }

    makeRaceId = (race: RaceDefinitionContract): string => {
        return `${race.entityRaceId}-${race.entityRaceTypeId}`;
    };

    transformLoadedRaces = (
        data: Array<RaceDefinitionContract> | null,
    ): Array<Pick<RaceItemsLazyOrFilteredState, 'type' | 'id' | 'entityTypeId' | 'name' | 'entity'>> => {
        const { currentRace } = this.props;
        let raceDefinitions: Array<RaceDefinitionContract> = data ? [...data] : [];
        if (currentRace) {
            if (
                !raceDefinitions.some(
                    (raceDef) => RaceUtils.getEntityRaceId(raceDef) === RaceUtils.getEntityRaceId(currentRace),
                )
            ) {
                raceDefinitions.push(currentRace);
            }
        }

        return raceDefinitions.map((race) => ({
            type: 'race',
            id: this.makeRaceId(race),
            entityTypeId: race.entityRaceTypeId,
            name: race.fullName,
            entity: race,
        }));
    };

    resetUi = () => {
        if (this.listingContent.current) {
            this.listingContent.current.scrollTop = 0;
        }
    };

    disableItems = (
        items: Array<Pick<RaceItemsLazyOrFilteredState, 'type' | 'id' | 'entityTypeId' | 'name' | 'entity'>>,
    ): Array<RaceItemsLazyOrFilteredState> => {
        const { currentRace } = this.props;
        const disabledItemIds = currentRace
            ? items.filter((item) => item.id === this.makeRaceId(currentRace)).map((item) => item.id)
            : [];

        return items.map((item) => ({
            ...item,
            disabled: disabledItemIds.indexOf(item.id) > -1,
        }));
    };

    groupItems = (items: Array<RaceItemsLazyOrFilteredState>): Array<FilteredRaceGroup | FilteredRaceItem> => {
        const { ruleData } = this.props;
        const groupData = RuleDataUtils.getRaceGroups(ruleData);
        let groupableItems: Array<RaceItemsLazyOrFilteredState> = [];
        let ungroupedItems: Array<FilteredRaceItem> = [];
        items.forEach((item) => {
            if (item.entity.groupIds?.length) {
                item.entity.groupIds.forEach((groupId) => {
                    groupableItems.push({
                        ...item,
                        groupId,
                    });
                });
            } else {
                ungroupedItems.push({
                    type: FILTER_LIST_ITEM_TYPE.ITEM,
                    item,
                    sortString: item.name ?? '',
                    key: `${FILTER_LIST_ITEM_TYPE.ITEM}-${item.id}`,
                });
            }
        });

        let groupDataLookup = keyBy(groupData, 'id');
        let groups: Array<FilteredRaceGroup | FilteredRaceItem> = [];
        let groupedItems = groupBy(groupableItems, 'groupId');
        Object.keys(groupedItems).forEach((groupKey) => {
            let groupItems = groupedItems[groupKey];
            if (groupItems.length === 1) {
                groups.push({
                    type: FILTER_LIST_ITEM_TYPE.ITEM,
                    item: groupItems[0],
                    sortString: groupItems[0].name ?? '',
                    key: `${FILTER_LIST_ITEM_TYPE.ITEM}-${groupItems[0].id}`,
                });
            } else {
                groups.push({
                    type: FILTER_LIST_ITEM_TYPE.GROUP,
                    items: orderBy(groupedItems[groupKey], (item) => item.name),
                    sortString: groupDataLookup[groupKey].name ?? '',
                    key: `${FILTER_LIST_ITEM_TYPE.GROUP}-${groupDataLookup[groupKey].id}`,
                    ...groupDataLookup[groupKey],
                });
            }
        });

        let listItems: Array<FilteredRaceGroup | FilteredRaceItem> = [...groups, ...ungroupedItems];

        return orderBy(listItems, 'sortString');
    };

    getFilteredItems = (query: string): Array<RaceItemsLazyOrFilteredState> => {
        const { lazyItems } = this.state;
        const { currentSourceFilter } = this.props;

        let filteredItems = lazyItems;

        if (currentSourceFilter) {
            const filteredIds =
                currentSourceFilter === HOMEBREW_ID
                    ? lazyItems.filter((item) => !item.entity.isHomebrew).map((item) => item.id)
                    : lazyItems
                          .filter((item) => {
                              let sources = (item.entity as RaceDefinitionContract).sources;
                              if (sources === null) {
                                  return true;
                              }
                              return !sources.some(
                                  (sourceMapping) => sourceMapping.sourceId === parseInt(currentSourceFilter),
                              );
                          })
                          .map((item) => item.id);

            filteredItems = lazyItems.filter((item) => !(filteredIds.indexOf(item.id) > -1));
        }

        return filteredItems.filter((item) => {
            const headingTag = item.name;
            const tagsElem = headingTag ? [headingTag] : [];

            return FilterUtils.doesQueryMatchData(query, item.entity.baseRaceName, tagsElem);
        });
    };

    handleQueryChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
        const query = evt.target.value;

        this.setState({
            query,
            filteredGroups: this.groupItems(this.getFilteredItems(query)),
        });

        this.resetUi();
    };

    renderUi = (): React.ReactNode => {
        const { query, filteredGroups } = this.state;
        const { ruleData, onRaceSelected } = this.props;
        const fallbackImageUrl = RuleDataUtils.getDefaultRaceImageUrl(ruleData) ?? '';
        return (
            <div className="filter-listing-ui">
                <div className="filter-constraints">
                    <div className="filter-constraint">
                        <input
                            type="search"
                            className="filter-constraint-query"
                            value={query}
                            onChange={this.handleQueryChange}
                            placeholder={'Search...'}
                            spellCheck={false}
                            autoComplete={'off'}
                        />
                    </div>
                </div>
                <div className="filter-listing-content" ref={this.listingContent}>
                    {!filteredGroups.length ? (
                        <div className="filter-listing-no-results">No Results Found</div>
                    ) : (
                        <div className="filter-listing-entries">
                            {filteredGroups.map((listItem) => {
                                switch (listItem.type) {
                                    case FILTER_LIST_ITEM_TYPE.ITEM:
                                        listItem = listItem as FilteredRaceItem;
                                        return (
                                            <FilterListingItem
                                                key={listItem.key}
                                                race={listItem.item.entity}
                                                disabled={listItem.item.disabled}
                                                onRaceSelected={(race) => onRaceSelected(race)}
                                                ruleData={ruleData}
                                                defaultImageUrl={fallbackImageUrl}
                                            />
                                        );
                                    case FILTER_LIST_ITEM_TYPE.GROUP:
                                        listItem = listItem as FilteredRaceGroup;
                                        return (
                                            <FilterListingGroup
                                                key={listItem.key}
                                                items={listItem.items}
                                                name={listItem.name}
                                                avatarUrl={listItem.avatarUrl}
                                                onRaceSelected={(race) => onRaceSelected(race)}
                                                defaultImageUrl={fallbackImageUrl}
                                                ruleData={ruleData}
                                            />
                                        );
                                }

                                return null;
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    render() {
        const { itemsLoaded, itemsLoading } = this.state;

        return (
            <div className="filter-listing">
                {itemsLoading && (
                    <div className="filter-listing-loading">
                        <LoadingPlaceholder label={<div className="filter-listing-loading-text">Loading...</div>} />
                    </div>
                )}
                {!itemsLoading && itemsLoaded && this.renderUi()}
            </div>
        );
    }
}
